// Gemini model catalog — pure logic + the single static fallback list.
//
// Shared by the backend (src/api/services/geminiModelCatalog.ts: fetch + cache)
// and the frontend (offline fallback + grouping in the model selector).
// CONSTRAINTS:
//   - no imports (no env, no node modules): the file must work in the browser
//     bundle AND inside the API Docker image, which only contains src/api/;
//   - do not move it under api/{engine,config,services,routes} — the client
//     isolation test (src/__tests__/engineIsolation.test.ts) forbids those.

export type GeminiFamily = 'pro' | 'flash' | 'flash-lite' | 'other';
export type GeminiChannel = 'stable' | 'preview' | 'exp';
export type GeminiCatalogSource = 'api' | 'cache' | 'fallback';

/** Subset of the ListModels `Model` resource that we read. */
export interface RawGeminiModel {
  name: string; // "models/gemini-2.5-flash"
  displayName?: string;
  description?: string;
  inputTokenLimit?: number;
  outputTokenLimit?: number;
  supportedGenerationMethods?: string[];
  thinking?: boolean;
}

export interface GeminiCatalogModel {
  id: string;
  /** Display name (kept as `name` too for the legacy `Model` shape). */
  name: string;
  displayName: string;
  description: string;
  family: GeminiFamily;
  /** "3.5", "2.5", "3" — null for version-less aliases (gemini-flash-latest). */
  version: string | null;
  channel: GeminiChannel;
  /** `-latest` alias (moves silently to a newer model). */
  isAlias: boolean;
  /** Newest (top-sorted, non-alias) model of its family. */
  isNewest: boolean;
  inputTokenLimit: number | null;
  outputTokenLimit: number | null;
  /** `thinking` as returned by the API; null when the API does not say. */
  thinking: boolean | null;
  supportedGenerationMethods: string[];
}

export interface GeminiCategory {
  id: GeminiFamily;
  label: string;
}

export interface GeminiCatalog {
  fetchedAt: string | null;
  source: GeminiCatalogSource;
  categories: GeminiCategory[];
  models: GeminiCatalogModel[];
}

/** Category order in the selector + Polish labels. */
export const GEMINI_CATEGORIES: readonly GeminiCategory[] = [
  { id: 'pro', label: 'Pro — najdokładniejsze' },
  { id: 'flash', label: 'Flash — szybkie' },
  { id: 'flash-lite', label: 'Flash-Lite — najtańsze' },
  { id: 'other', label: 'Inne' },
];

/**
 * ONE place to tweak what is hidden from the catalog. An id is dropped when any
 * of its `-`-separated tokens is on this list (token match, not substring —
 * "live" must not hit "delivery"). All of these expose generateContent in
 * ListModels but are not text-generation models useful for a clinical report.
 */
export const GEMINI_EXCLUDED_ID_TOKENS: readonly string[] = [
  'embedding',   // gemini-embedding-001 / -2
  'aqa',         // attributed QA
  'imagen',      // image generation
  'veo',         // video generation
  'tts',         // text-to-speech (gemini-2.5-flash-preview-tts, gemini-3.8-flash-tts)
  'live',        // Live API (bidi) models
  'native',      // native-audio
  'audio',
  'image',       // image generation ("Nano Banana": gemini-2.5-flash-image, gemini-3-pro-image-preview)
  'generation',  // "image-generation" variants
  'transcribe',  // speech-to-text
  'customtools', // gemini-3.1-pro-preview-customtools (agentic tool variant)
  'computer',    // computer-use
  'robotics',    // robotics-er
  'omni',        // any-to-any multimodal output models
];

const FAMILY_ORDER: Record<GeminiFamily, number> = { pro: 0, flash: 1, 'flash-lite': 2, other: 3 };
const CHANNEL_ORDER: Record<GeminiChannel, number> = { stable: 0, preview: 1, exp: 2 };

// gemini-<version>-<family>[-suffix]; flash-lite before flash (alternation order).
const VERSIONED_RE = /^gemini-(\d+(?:\.\d+)*)-(pro|flash-lite|flash)(?:-(.+))?$/;
// Version-less aliases: gemini-flash-latest, gemini-flash-lite-latest, gemini-pro-latest.
const ALIAS_RE = /^gemini-(pro|flash-lite|flash)-latest$/;
// Syntactic guard for anything that ends up in the Google URL path.
const SAFE_ID_RE = /^[a-z0-9][a-z0-9.-]{0,99}$/;

/** Strips the "models/" prefix the ListModels API puts on names. */
export function normalizeGeminiModelId(model: unknown): string | null {
  if (typeof model !== 'string') return null;
  const id = model.trim().replace(/^models\//, '');
  return SAFE_ID_RE.test(id) ? id : null;
}

export function isExcludedGeminiId(id: string): boolean {
  const tokens = id.toLowerCase().split('-');
  return tokens.some((t) => GEMINI_EXCLUDED_ID_TOKENS.includes(t));
}

interface ParsedId {
  family: GeminiFamily;
  version: string | null;
  versionParts: number[];
  channel: GeminiChannel;
  isAlias: boolean;
  /** Suffix tokens that are neither channel nor date nor pin (e.g. "thinking"). */
  variant: string;
  /** YYYYMM for "-09-2025" style dates, MMDD for "-0925"/"-1206"; 0 = undated. */
  dateKey: number;
  /** "-001" style pinned stable snapshot. */
  pinned: boolean;
}

export function parseGeminiModelId(id: string): ParsedId {
  const alias = ALIAS_RE.exec(id);
  if (alias) {
    return { family: alias[1] as GeminiFamily, version: null, versionParts: [], channel: 'stable', isAlias: true, variant: '', dateKey: 0, pinned: false };
  }
  const m = VERSIONED_RE.exec(id);
  if (!m) {
    // Unknown layout (e.g. a new product line) — "other", best-effort version.
    const v = /-(\d+(?:\.\d+)*)(?:-|$)/.exec(id);
    const tokens = id.split('-');
    const channel: GeminiChannel = tokens.includes('exp') ? 'exp' : tokens.includes('preview') ? 'preview' : 'stable';
    return { family: 'other', version: v ? v[1] : null, versionParts: v ? v[1].split('.').map(Number) : [], channel, isAlias: tokens.includes('latest'), variant: id, dateKey: 0, pinned: false };
  }
  const version = m[1];
  const family = m[2] as GeminiFamily;
  const suffix = m[3] ? m[3].split('-') : [];
  let channel: GeminiChannel = 'stable';
  let isAlias = false;
  let dateKey = 0;
  let pinned = false;
  const variant: string[] = [];
  for (let i = 0; i < suffix.length; i++) {
    const t = suffix[i];
    if (t === 'exp') channel = 'exp';
    else if (t === 'preview') { if (channel !== 'exp') channel = 'preview'; }
    else if (t === 'latest') isAlias = true;
    else if (/^\d{2}$/.test(t) && /^\d{4}$/.test(suffix[i + 1] ?? '')) { // 09-2025
      dateKey = Number(suffix[i + 1]) * 100 + Number(t);
      i++;
    } else if (/^\d{4}$/.test(t)) dateKey = Math.max(dateKey, Number(t)); // 0925 / 1206
    else if (/^\d{3}$/.test(t)) pinned = true; // -001
    else variant.push(t);
  }
  // A dated snapshot without an explicit channel is still not a stable release.
  if (dateKey > 0 && channel === 'stable') channel = 'preview';
  return { family, version, versionParts: version.split('.').map(Number), channel, isAlias, variant: variant.join('-'), dateKey, pinned };
}

function compareVersionsDesc(a: number[], b: number[]): number {
  const len = Math.max(a.length, b.length);
  for (let i = 0; i < len; i++) {
    const d = (b[i] ?? 0) - (a[i] ?? 0);
    if (d !== 0) return d;
  }
  return 0;
}

/** True for Gemini pro/flash/flash-lite models >= 2.5 (all support thinking). */
export function isThinkingCapableGeminiId(model: string): boolean {
  const id = normalizeGeminiModelId(model);
  if (!id) return false;
  const p = parseGeminiModelId(id);
  if (p.family === 'other') return false;
  if (p.version === null) return p.isAlias; // -latest aliases point at current (>= 2.5) models
  return compareVersionsDesc(p.versionParts, [2, 5]) <= 0; // version >= 2.5
}

/** Keeps text-generation `gemini-*` models only (see GEMINI_EXCLUDED_ID_TOKENS). */
export function filterGeminiTextModels(raw: RawGeminiModel[]): RawGeminiModel[] {
  return raw.filter((m) => {
    const id = normalizeGeminiModelId(m?.name);
    if (!id || !id.startsWith('gemini-')) return false;
    if (!Array.isArray(m.supportedGenerationMethods) || !m.supportedGenerationMethods.includes('generateContent')) return false;
    return !isExcludedGeminiId(id);
  });
}

interface Entry { model: GeminiCatalogModel; parsed: ParsedId }

function compareEntries(a: Entry, b: Entry): number {
  // Version-less aliases always last.
  const aNoVer = a.parsed.version === null ? 1 : 0;
  const bNoVer = b.parsed.version === null ? 1 : 0;
  if (aNoVer !== bNoVer) return aNoVer - bNoVer;
  const v = compareVersionsDesc(a.parsed.versionParts, b.parsed.versionParts);
  if (v !== 0) return v;
  const c = CHANNEL_ORDER[a.parsed.channel] - CHANNEL_ORDER[b.parsed.channel];
  if (c !== 0) return c;
  const al = Number(a.parsed.isAlias) - Number(b.parsed.isAlias);
  if (al !== 0) return al;
  const va = Number(a.parsed.variant !== '') - Number(b.parsed.variant !== '');
  if (va !== 0) return va;
  const pin = Number(a.parsed.pinned) - Number(b.parsed.pinned);
  if (pin !== 0) return pin;
  // Undated first, then newest date first.
  if (a.parsed.dateKey !== b.parsed.dateKey) {
    if (a.parsed.dateKey === 0) return -1;
    if (b.parsed.dateKey === 0) return 1;
    return b.parsed.dateKey - a.parsed.dateKey;
  }
  return a.model.id.localeCompare(b.model.id);
}

function toCatalogModel(raw: RawGeminiModel, id: string, p: ParsedId): GeminiCatalogModel {
  const displayName = raw.displayName || id;
  return {
    id,
    name: displayName,
    displayName,
    description: raw.description || '',
    family: p.family,
    version: p.version,
    channel: p.channel,
    isAlias: p.isAlias,
    isNewest: false,
    inputTokenLimit: typeof raw.inputTokenLimit === 'number' ? raw.inputTokenLimit : null,
    outputTokenLimit: typeof raw.outputTokenLimit === 'number' ? raw.outputTokenLimit : null,
    thinking: typeof raw.thinking === 'boolean' ? raw.thinking : null,
    supportedGenerationMethods: raw.supportedGenerationMethods ?? [],
  };
}

/**
 * Filter → categorize → dedupe → sort → flag newest.
 *
 * Dedupe rules (the underlying ids stay callable — see allowedIds):
 *  - version-less `-latest` aliases are hidden when the family has any versioned model
 *    (an alias silently changes the model — bad for report reproducibility);
 *  - within one family+version+variant, when a plain stable release exists
 *    (gemini-3.1-flash-lite), its preview / dated / pinned / -latest siblings are hidden.
 */
export function buildGeminiModelList(raw: RawGeminiModel[]): { models: GeminiCatalogModel[]; allowedIds: string[] } {
  const seen = new Set<string>();
  const entries: Entry[] = [];
  for (const r of filterGeminiTextModels(raw)) {
    const id = normalizeGeminiModelId(r.name)!;
    if (seen.has(id)) continue;
    seen.add(id);
    const parsed = parseGeminiModelId(id);
    entries.push({ model: toCatalogModel(r, id, parsed), parsed });
  }
  const allowedIds = entries.map((e) => e.model.id);

  const groupKey = (p: ParsedId) => `${p.family}|${p.version}|${p.variant}`;
  const isCanonical = (p: ParsedId) => p.channel === 'stable' && !p.isAlias && !p.pinned && p.dateKey === 0;
  const canonicalGroups = new Set(entries.filter((e) => isCanonical(e.parsed)).map((e) => groupKey(e.parsed)));
  const versionedFamilies = new Set(entries.filter((e) => e.parsed.version !== null && e.parsed.family !== 'other').map((e) => e.parsed.family));

  const kept = entries.filter((e) => {
    if (e.parsed.version === null && e.parsed.isAlias) return !versionedFamilies.has(e.parsed.family);
    if (canonicalGroups.has(groupKey(e.parsed))) return isCanonical(e.parsed);
    return true;
  });

  kept.sort((a, b) => {
    const f = FAMILY_ORDER[a.parsed.family] - FAMILY_ORDER[b.parsed.family];
    return f !== 0 ? f : compareEntries(a, b);
  });

  const flagged = new Set<GeminiFamily>();
  for (const e of kept) {
    if (flagged.has(e.parsed.family) || e.parsed.version === null) continue;
    e.model.isNewest = true;
    flagged.add(e.parsed.family);
  }
  return { models: kept.map((e) => e.model), allowedIds };
}

/** Categories that actually have models, in GEMINI_CATEGORIES order. */
export function categoriesFor(models: Array<{ family?: GeminiFamily }>): GeminiCategory[] {
  const present = new Set(models.map((m) => m.family ?? 'other'));
  return GEMINI_CATEGORIES.filter((c) => present.has(c.id)).map((c) => ({ ...c }));
}

/** Groups models by category (for <optgroup>); models without family go to "other". */
export function groupGeminiModels<T extends { family?: GeminiFamily }>(
  models: T[],
  categories: GeminiCategory[] = [...GEMINI_CATEGORIES],
): Array<{ category: GeminiCategory; models: T[] }> {
  const known = new Set(categories.map((c) => c.id));
  return categories
    .map((category) => ({
      category,
      models: models.filter((m) => {
        const fam = m.family ?? 'other';
        return fam === category.id || (category.id === 'other' && !known.has(fam));
      }),
    }))
    .filter((g) => g.models.length > 0);
}

/**
 * THE static fallback list (single source of truth for backend + frontend).
 * Used when the ListModels API is unreachable and nothing is cached.
 * Pruned 2026-09-30 to ids that the API actually listed (the older hardcoded
 * lists carried 3.5-pro / 3.1-pro / 3.1-flash / 3-pro-preview, which do not exist).
 * Must contain DEFAULT_GEMINI_MODEL, FALLBACK_MODEL and GEMINI_FALLBACK_CHAIN from src/config.ts.
 */
export const GEMINI_FALLBACK_RAW: readonly RawGeminiModel[] = [
  { name: 'models/gemini-3.1-pro-preview', displayName: 'Gemini 3.1 Pro Preview', description: 'Zaawansowane rozumowanie kliniczne (preview)' },
  { name: 'models/gemini-2.5-pro', displayName: 'Gemini 2.5 Pro', description: 'Pro — najsilniejsze rozumowanie + Thinking' },
  { name: 'models/gemini-3.5-flash', displayName: 'Gemini 3.5 Flash', description: 'Szybki model 3.5 (z thinkingiem potrafi zużyć cały budżet tokenów)' },
  { name: 'models/gemini-3-flash-preview', displayName: 'Gemini 3 Flash Preview', description: 'Preview Gemini 3 Flash' },
  { name: 'models/gemini-2.5-flash', displayName: 'Gemini 2.5 Flash', description: 'Flash — streaming + Thinking Mode' },
  { name: 'models/gemini-3.5-flash-lite', displayName: 'Gemini 3.5 Flash-Lite', description: 'Najtańszy i najszybszy model 3.5' },
  { name: 'models/gemini-3.1-flash-lite', displayName: 'Gemini 3.1 Flash-Lite', description: 'Wydajny i ekonomiczny' },
  { name: 'models/gemini-2.5-flash-lite', displayName: 'Gemini 2.5 Flash-Lite', description: 'Najtańszy model 2.5' },
].map((m) => ({ ...m, supportedGenerationMethods: ['generateContent'] }));

export const GEMINI_FALLBACK_IDS: readonly string[] = GEMINI_FALLBACK_RAW.map((m) => normalizeGeminiModelId(m.name)!);

export function buildFallbackCatalog(): GeminiCatalog {
  const { models } = buildGeminiModelList([...GEMINI_FALLBACK_RAW]);
  return { fetchedAt: null, source: 'fallback', categories: categoriesFor(models), models };
}
